import { IProduct, ManagementConfig } from "types/productManagementTypes";
import { emptyManagementConfig, mergeStoredConfig } from "./ManagementConfigForm";
import { TIMEOUT_ENUM_SECONDS } from "./DeviceStatesPanel";

const boolFromState = (value: unknown): boolean | undefined => {
  if (value === "1" || value === 1 || value === true) return true;
  if (value === "0" || value === 0 || value === false) return false;
  return undefined;
};

const numberFromState = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

// GetSleepDelay/GetScreenOffDelay report the C# enum name; 0 means never here.
const delayFromState = (value: unknown): number | undefined => {
  if (value === undefined || value === null) return undefined;
  const raw = String(value).trim();
  if (raw === "") return undefined;
  if (Object.prototype.hasOwnProperty.call(TIMEOUT_ENUM_SECONDS, raw)) {
    return TIMEOUT_ENUM_SECONDS[raw];
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed === 0 ? -1 : parsed;
};

// A device may report fields the web app does not model; they must not reach the form.
const pickKnownFields = (config?: Partial<ManagementConfig> | null): Partial<ManagementConfig> => {
  if (!config) return {};
  const known = Object.keys(emptyManagementConfig()) as (keyof ManagementConfig)[];
  const picked: Partial<ManagementConfig> = {};
  known.forEach((key) => {
    if (config[key] !== undefined) {
      (picked as Record<string, unknown>)[key] = config[key];
    }
  });
  return picked;
};

// Precedence, lowest first: form defaults, the config saved here, the config on
// the device, then its live reported state.
export const deviceStateToManagementConfig = (
  product: IProduct | null,
  savedConfig?: Partial<ManagementConfig> | null,
): ManagementConfig => {
  const states = (product?.states ?? {}) as Record<string, unknown>;
  const onDeviceConfig = states.managementConfig as Partial<ManagementConfig> | undefined;
  const config = mergeStoredConfig({
    ...pickKnownFields(savedConfig),
    ...pickKnownFields(onDeviceConfig),
  });

  const brightness = numberFromState(states.brightness);
  if (brightness !== undefined) config.brightness = brightness;

  const sleepDelayTime = delayFromState(states.sleepDelay);
  if (sleepDelayTime !== undefined) config.sleepDelayTime = sleepDelayTime;

  const screenOffDelayTime = delayFromState(states.screenOffDelay);
  if (screenOffDelayTime !== undefined) config.screenOffDelayTime = screenOffDelayTime;

  const reportedFlags: [keyof ManagementConfig, unknown][] = [
    ["usbDebugging", states.usbDebugging],
    ["systemUpdateOn", states.systemUpdate],
    ["wifiAlwaysOn", states.wifiAlwaysOn],
    ["playBoundary", states.playBoundary],
  ];
  reportedFlags.forEach(([key, raw]) => {
    const parsed = boolFromState(raw);
    if (parsed !== undefined) {
      (config as unknown as Record<string, unknown>)[key] = parsed;
    }
  });

  if (typeof states.appAsHome === "string") {
    config.setAppAsHome = states.appAsHome.trim() !== "";
  }

  return config;
};

export const hasReportedState = (product: IProduct | null): boolean =>
  Object.keys((product?.states ?? {}) as Record<string, unknown>).length > 0;
